import { useQueryClient } from '@tanstack/react-query';
import { Tab, Tabs } from '@unistocks/components/tabs/tabs';
import { toast } from '@unistocks/components/toast';
import {
  EditPortfolioForm,
  EditPortfolioFormProps
} from '@unistocks/features/portfolios/components/forms/edit-portfolio-form';
import { PortfolioExternalAccounts } from '@unistocks/features/portfolios/components/portfolio-settings/portfolio-external-accounts/portfolio-external-accounts';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { isPortfolio as isPortfolioCheck } from '@unistocks/features/portfolios/portfolio-utils';
import { t } from '@unistocks/localization';
import {
  setPortfoliosGetData,
  usePortfoliosUpdateMutation
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  setWatchlistsGetData,
  useWatchlistsUpdateMutation
} from '@unistocks/proxy/axios-client/WatchlistsQuery';
import React, { useCallback, useMemo } from 'react';
import Sortable from 'react-native-sortables';

export type PortfolioSettingsProps = {
  portfolio: PortfolioBase;
  onSave: () => void;
};

export const PortfolioSettings: React.FC<PortfolioSettingsProps> = ({ portfolio, onSave }) => {
  const queryClient = useQueryClient();
  const isPortfolio = isPortfolioCheck(portfolio);

  const { mutateAsync: updatePortfolioMutate, isPending: isLoadingPortfolioUpdate } =
    usePortfoliosUpdateMutation(portfolio.id, {
      onSuccess: data => {
        setPortfoliosGetData(queryClient, () => data, portfolio.id);
        toast.success(t('::SuccessfullyUpdatedPortfolio', { 0: data.name }));
        onSave();
      }
    });

  const { mutateAsync: updateWatchlistMutate, isPending: isLoadingWatchlistUpdate } =
    useWatchlistsUpdateMutation(portfolio.id, {
      onSuccess: data => {
        setWatchlistsGetData(queryClient, () => data, portfolio.id);
        toast.success(t('::SuccessfullyUpdatedWatchlist', { 0: data.name }));
        onSave();
      }
    });

  const onSubmit = useCallback<EditPortfolioFormProps['onSubmit']>(
    async ({ name, tableColumns }) => {
      const columns = tableColumns.map(col => col.value);

      if (isPortfolio) {
        await updatePortfolioMutate({
          name,
          tableColumns: columns,
          portfolioComparisonSettings: null
        });
      } else {
        await updateWatchlistMutate({
          name,
          tableColumns: columns,
          portfolioComparisonSettings: null
        });
      }
    },
    [isPortfolio, updatePortfolioMutate, updateWatchlistMutate]
  );

  const tabs = useMemo<Tab[]>(() => {
    const tabs: Tab[] = [
      {
        value: 'settings',
        label: t('::Settings')
      }
    ];

    if (isPortfolio) {
      tabs.push({
        value: 'external-accounts',
        label: t('::ExternalAccounts'),
        disabled: !portfolio.syncedPortfolios.length
      });
    }

    return tabs;
  }, [isPortfolio, portfolio]);

  const renderTab = useCallback(
    (tab: string) => {
      switch (tab) {
        case 'settings':
          return (
            <Sortable.PortalProvider>
              <EditPortfolioForm
                portfolio={portfolio}
                isSubmitting={isPortfolio ? isLoadingPortfolioUpdate : isLoadingWatchlistUpdate}
                onSubmit={onSubmit}
              />
            </Sortable.PortalProvider>
          );

        case 'external-accounts':
          if (isPortfolio) {
            return <PortfolioExternalAccounts portfolio={portfolio} />;
          }
          return null;
      }

      return null;
    },
    [isLoadingPortfolioUpdate, isLoadingWatchlistUpdate, isPortfolio, onSubmit, portfolio]
  );

  return <Tabs hideSingleTab tabs={tabs} renderTab={renderTab} flex={1} />;
};
